/**
 * `<SessionTranscriptAccordion>` — collapsed-by-default panel mounted near
 * the bottom of `<ProgressNoteViewer>` for scribe-backed Progress Notes.
 *
 * Purpose
 *   Show the raw, diarized session transcript that the AI scribe used to
 *   author the structured Progress Note. Doctors review the transcript to
 *   verify what the AI captured (or missed) against what was actually said.
 *
 * Data path
 *   1. The Progress Note's sibling `Provenance.target[]` carries a reference
 *      to the ScribeTranscript `DocumentReference` written by the BE finalize
 *      handler (`packages/middleware/src/scribe/handlers/finalize-session.handler.ts`).
 *   2. That DocumentReference's `content[0].attachment.url` points at a
 *      `Binary` resource holding the transcript as `text/plain`.
 *   3. We read the DocRef, confirm it's a ScribeTranscript (by the
 *      `https://behuman.health/document-type#ScribeTranscript` tag), then
 *      download the transcript via `downloadAttachment(...)` and decode to a
 *      string.
 *
 * Lazy fetch
 *   The query is enabled only when the user opens the accordion. The default
 *   collapsed state means notes that the doctor never expands cost zero
 *   network traffic. `staleTime: 5 * 60_000` so repeat opens within the same
 *   chart session don't re-fetch.
 *
 * Render
 *   The transcript text is line-delimited (`Doctor: …\nPatient: …\n`). We
 *   split into lines, parse the optional `Speaker: text` prefix, and render
 *   each line through `<TranscriptBubble variant="light" />` so the styling
 *   matches the legacy transcript panel.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Accordion, Anchor, Center, Loader, Stack, Text } from '@mantine/core'
import { useMedplum } from '@medplum/react'
import { formatHumanName, type MedplumClient } from '@medplum/core'
import type { DocumentReference, Provenance } from '@medplum/fhirtypes'
import { normalizeSpeakerRole, TranscriptBubble } from '@/components/scribe/TranscriptBubble'
import { logError } from '@/utils/errorUtils'
import { SCRIBE_LANGUAGE_LABELS, isScribeSourceLanguage } from '@behuman/core-contract'
import { downloadAttachment } from '@/utils/downloadAttachment'

/** Internal tag the BE finalize handler stamps on the ScribeTranscript DocRef. */
const SCRIBE_TRANSCRIPT_SYSTEM = 'https://behuman.health/document-type'
const SCRIBE_TRANSCRIPT_CODE = 'ScribeTranscript'

type TranscriptLine = {
  /** Pre-formatted clock time like `2:54 PM`, extracted from the `[h:mm A]`
   *  prefix the BE writes per chunk. Empty for legacy lines that pre-date
   *  the per-line-timestamp change. */
  time?: string
  speaker?: string
  /** Detected role for this line. Resolved at parse-time from the speaker
   *  string BEFORE the canonical-FHIR-name swap so it survives the swap
   *  (otherwise "Noah Kim" would heuristically resolve to "provider"). A
   *  `care coordinator` line keeps its raw speaker — no FHIR-name swap. */
  role?: 'provider' | 'patient' | 'coordinator'
  text: string
}

/**
 * Extract the linked Encounter id from a Progress Note's sibling Provenance.
 *
 * Why entity[].what, not target[]: the BE `evaluateSoapNote` cascade
 * (`visit.service.ts`) writes all resources it CREATED into `Provenance.target[]`
 * (Conditions, Procedures, Claim, QR, Progress Note DocRef) and writes the
 * source Encounter into `Provenance.entity[].what` (role: 'source'). The
 * pre-existing ScribeTranscript DocRef written by the finalize step is NOT
 * added to either array, so we search for it independently by encounter id.
 */
function findEncounterIdFromProvenance(provenance: Provenance | undefined): string | null {
  for (const e of provenance?.entity ?? []) {
    const ref = e.what?.reference
    if (ref?.startsWith('Encounter/')) {
      const id = ref.split('/')[1]
      if (id) return id
    }
  }
  return null
}

/**
 * Search the Medplum index for the ScribeTranscript DocumentReference linked
 * to the given encounter id. The finalize handler stamps every transcript
 * DocRef with `context.encounter[0].reference = Encounter/<id>` AND the
 * `https://behuman.health/document-type#ScribeTranscript` tag — search by
 * encounter, then filter by tag.
 *
 * Why search-by-encounter (not via Provenance.target[]): the BE cascade
 * doesn't add the pre-existing transcript DocRef into the Provenance, so
 * walking `target[]` returns nothing useful. The encounter→docref search is
 * also resilient to legacy notes saved before this code path existed.
 */
async function loadTranscriptByEncounter(
  medplum: MedplumClient,
  encounterId: string,
): Promise<{ docRef: DocumentReference; patientName: string | null } | null> {
  try {
    const candidates = await medplum.searchResources('DocumentReference', {
      encounter: `Encounter/${encounterId}`,
      _count: '20',
      _sort: '-_lastUpdated',
    })
    const docRef = candidates.find((d) =>
      d.type?.coding?.some(
        (coding) =>
          coding.system === SCRIBE_TRANSCRIPT_SYSTEM && coding.code === SCRIBE_TRANSCRIPT_CODE,
      ),
    )
    if (!docRef) return null

    // Resolve the patient name from the DocRef's `subject`. We need it so
    // the rendered transcript labels the patient by their canonical FHIR
    // name (e.g. "Noah Kim") instead of the literal "patient" string the
    // mock-mode hook emits — and so real-Zoom display names (which already
    // ARE the canonical name) round-trip consistently.
    const patientRef = docRef.subject?.reference
    const patientId = patientRef?.startsWith('Patient/') ? patientRef.split('/')[1] : undefined
    let patientName: string | null = null
    if (patientId) {
      try {
        const patient = await medplum.readResource('Patient', patientId)
        const first = patient.name?.[0]
        patientName = first ? formatHumanName(first) : null
      } catch (err) {
        logError('session-transcript', `read Patient/${patientId}`, err)
      }
    }
    return { docRef, patientName }
  } catch (err) {
    logError('session-transcript', `search DocRef by encounter ${encounterId}`, err)
    return null
  }
}

/**
 * Pull the practitioner's display name from `Provenance.agent[]`. The BE
 * `evaluateSoapNote` stamps the recorder + performer + onBehalfOf agents
 * with the practitioner reference + display, so this is a pure synchronous
 * extract — no Medplum read needed.
 *
 * Falls back to undefined when no Practitioner agent is on the provenance
 * (legacy / non-cascade-authored notes).
 */
function findPractitionerNameFromProvenance(
  provenance: Provenance | undefined,
): string | undefined {
  for (const agent of provenance?.agent ?? []) {
    const ref = agent.who?.reference
    if (ref?.startsWith('Practitioner/') && agent.who?.display) {
      return agent.who.display
    }
  }
  return undefined
}

/**
 * Convert a plain-text transcript (`Doctor: …\nPatient: …`) into per-line
 * `{speaker, text}` records. Lines without a `Speaker:` prefix render as
 * unattributed text.
 */
function parseTranscriptText(raw: string): TranscriptLine[] {
  // Match three optional segments:
  //   1. `[clock time]` — e.g. `[2:54 PM]`. The BE writes this prefix from
  //      `finalize-session.handler.ts:formatChunkTime` when a chunk has a
  //      timestamp; legacy transcripts pre-date this and omit it.
  //   2. `Speaker:` — short leading token followed by colon (one or two
  //      whitespace-free words). Constrained so we don't aggressively split
  //      on every colon (e.g. inside `BP: 120/80`).
  //   3. The caption body — everything after the prefixes.
  const LINE = /^(?:\[([^\]]+)\]\s+)?(?:([A-Za-z][\w .-]{0,30}):\s+)?(.+)$/

  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map<TranscriptLine>((line) => {
      const match = line.match(LINE)
      if (!match) return { text: line }
      const [, time, speaker, text] = match
      return {
        time: time || undefined,
        speaker: speaker || undefined,
        text: text || line,
      }
    })
}

type SessionTranscriptAccordionProps = {
  /** The Progress Note's sibling Provenance — used to find the linked Encounter. */
  provenance: Provenance
}

export function SessionTranscriptAccordion({ provenance }: SessionTranscriptAccordionProps) {
  const medplum = useMedplum()
  const [opened, setOpened] = useState(false)

  // Encounter id is the join key for finding the ScribeTranscript DocRef.
  // Cheap synchronous extract from the Provenance — if there's no linked
  // encounter we can't find a transcript, so don't bother rendering the
  // accordion at all.
  const encounterId = findEncounterIdFromProvenance(provenance)

  // Practitioner name is on the Provenance's agents — no fetch needed.
  // Used to relabel the "doctor" / "provider" prefix in transcript lines
  // with the canonical FHIR name.
  const practitionerName = useMemo(
    () => findPractitionerNameFromProvenance(provenance),
    [provenance],
  )

  const transcriptQuery = useQuery({
    queryKey: ['scribe-transcript', encounterId],
    queryFn: async (): Promise<{
      lines: TranscriptLine[]
      patientName: string | null
      // PD-1600 — the spoken language, so the panel says which language the
      // reader is comparing the English note against.
      language: string | null
    }> => {
      if (!encounterId) return { lines: [], patientName: null, language: null }
      const result = await loadTranscriptByEncounter(medplum, encounterId)
      if (!result) return { lines: [], patientName: null, language: null }
      const attachment = result.docRef.content?.[0]?.attachment
      const language = attachment?.language ?? null
      const url = attachment?.url
      if (!url) return { lines: [], patientName: result.patientName, language }
      // Pre-signed storage URL — plain-fetch via downloadAttachment; medplum.download's
      // auth headers force a CORS preflight the GET/HEAD-only storage CDN 403s.
      // The transcript DocRef WAS found, so a fetch/decode failure here is a real
      // error — rethrow it so the query enters its error state instead of
      // collapsing into the "No transcript on file." empty render.
      try {
        const blob = await downloadAttachment(medplum, url)
        const text = await blob.text()
        return { lines: parseTranscriptText(text), patientName: result.patientName, language }
      } catch (err) {
        logError('session-transcript', `download transcript ${url}`, err)
        throw err
      }
    },
    enabled: opened && Boolean(encounterId),
    staleTime: 5 * 60_000,
    retry: 1,
  })

  // Don't render the accordion at all if the provenance has no linked
  // encounter — there's no way to find a transcript without one.
  if (!encounterId) return null

  const rawLines = transcriptQuery.data?.lines ?? []
  const patientName = transcriptQuery.data?.patientName ?? null

  // Resolve role from the RAW speaker prefix first, then optionally swap
  // the speaker to the canonical FHIR name. Keeping `role` on the line lets
  // the bubble render the right pill even after we've replaced the speaker
  // string with a name (e.g. "Noah Kim") that would no longer match the
  // "patient" substring heuristic.
  const lines: TranscriptLine[] = rawLines.map((line) => {
    const role = normalizeSpeakerRole(line.speaker)
    let speaker = line.speaker
    if (role === 'provider' && practitionerName) speaker = practitionerName
    else if (role === 'patient' && patientName) speaker = patientName
    return { ...line, role, speaker }
  })

  // PD-1600 — a Spanish transcript is the reader's comparison source for an
  // English note, so the panel says so rather than looking like a plain
  // transcript. English is the norm and stays unlabelled.
  const transcriptLanguage = transcriptQuery.data?.language?.slice(0, 2).toLowerCase()
  const transcriptLabel =
    transcriptLanguage && transcriptLanguage !== 'en' && isScribeSourceLanguage(transcriptLanguage)
      ? `Session Transcript — ${SCRIBE_LANGUAGE_LABELS[transcriptLanguage]}`
      : 'Session Transcript'

  return (
    <Accordion
      variant="separated"
      multiple={false}
      value={opened ? 'transcript' : null}
      onChange={(value) => setOpened(value === 'transcript')}
      data-testid="session-transcript-accordion"
      styles={{
        item: {
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          background: '#fff',
        },
        control: {
          padding: '10px 14px',
        },
        label: {
          fontSize: 13,
          fontWeight: 600,
          color: '#374151',
        },
      }}
    >
      <Accordion.Item value="transcript">
        <Accordion.Control>{transcriptLabel}</Accordion.Control>
        <Accordion.Panel>
          {transcriptQuery.isLoading && (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          )}
          {!transcriptQuery.isLoading && transcriptQuery.isError && (
            <Text c="red" size="sm">
              Couldn&apos;t load the transcript.{' '}
              <Anchor
                size="sm"
                component="button"
                type="button"
                onClick={() => void transcriptQuery.refetch()}
              >
                Retry
              </Anchor>
            </Text>
          )}
          {!transcriptQuery.isLoading && !transcriptQuery.isError && lines.length === 0 && (
            <Text c="dimmed" size="sm">
              No transcript on file.
            </Text>
          )}
          {!transcriptQuery.isLoading && !transcriptQuery.isError && lines.length > 0 && (
            <div
              data-testid="session-transcript-list"
              style={{ maxHeight: 380, overflowY: 'auto' }}
            >
              <Stack gap={4}>
                {lines.map((line, idx) => (
                  <TranscriptBubble
                    key={idx}
                    variant="light"
                    speaker={line.speaker}
                    role={line.role}
                    text={line.text}
                    displayTime={line.time}
                  />
                ))}
              </Stack>
            </div>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  )
}
